import { Box, IconButton, TimesIcon, ValueAutocompleteField } from '@forgeglobal/design-framework'
import {
  ConditionStateType,
  FieldMap,
  booleanOptions,
  convertValueToConditionState,
  optionsForField,
  prettifyName,
  validateField,
} from 'util/SegmentUtils'
import { Form, FormRenderProps } from 'react-final-form'
import React, { useCallback, useEffect } from 'react'
import compact from 'lodash/compact'
import values from 'lodash/values'

import { ValueField } from './ValueField'

export const ConditionRowBuilder: React.FC<{
  fields: FieldMap
  onConditionChange: (condition: string) => void
  value: string
  onDelete?: () => void
}> = ({ fields, onConditionChange, value, onDelete }) => {
  const initialValues = convertValueToConditionState(value, fields)
  return (
    <Box display="flex">
      <Form<ConditionStateType>
        render={props => (
          <ConditionRowForm fields={fields} onConditionChange={onConditionChange} value={value} {...props} />
        )}
        onSubmit={() => void 0}
        initialValues={initialValues}
      />
      {onDelete && (
        <IconButton onClick={onDelete}>
          <TimesIcon />
        </IconButton>
      )}
    </Box>
  )
}

const ConditionRowForm: React.FC<FormRenderProps<ConditionStateType> & {
  fields: FieldMap
  onConditionChange: (condition: string) => void
  value: string
}> = ({ values: conditionState, form, fields, onConditionChange, value, initialValues }) => {
  const nameDropdownOptions = values(fields).map(field => ({
    label: prettifyName(field.simpleName),
    value: field.name,
  }))
  const currentFieldName = conditionState.name
  const currentField = currentFieldName ? fields[currentFieldName] : null
  const availableOps = currentField ? optionsForField(currentField) : []
  const baseValueOptions =
    currentField?.type === 'boolean'
      ? booleanOptions
      : (currentField && currentField.type === 'enum' ? currentField.options : []).map(fieldOption => ({
          label: prettifyName(fieldOption),
          value: fieldOption,
        }))
  const isMultiValue = currentField?.type === 'enum' && conditionState.operation === 'in'
  const isNullable = !!currentField?.isNullable
  const valueOptions = compact([...baseValueOptions, isNullable && { label: 'Blank', value: 'null' }])
  const valueIsValid = !!(currentField && validateField(conditionState, currentField))
  const changeIfNotOldValue = (newCondition: string) => {
    if (newCondition !== value) {
      onConditionChange(newCondition)
    }
  }
  const formChange = form.change
  useEffect(() => {
    if (availableOps.length === 1 && conditionState?.operation !== availableOps[0]?.value) {
      formChange('operation', availableOps[0].value)
    }
  }, [availableOps, conditionState, formChange])
  useEffect(() => {
    if (conditionState.name !== initialValues.name) {
      formChange('value', null)
    }
  }, [conditionState.name, formChange, initialValues.name])
  const callbackedChange = useCallback(changeIfNotOldValue, [onConditionChange, value])
  useEffect(() => {
    if (valueIsValid && conditionState.operation && conditionState.value && conditionState.name) {
      const nameOpFragment = `${conditionState.name} ${conditionState.operation}`
      if (Array.isArray(conditionState.value)) {
        const joinedValues = conditionState.value
          .map(selectedValue => {
            if (selectedValue.includes(' ')) {
              return `"${selectedValue}"`
            }
            return selectedValue
          })
          .join(',')
        callbackedChange(`${nameOpFragment} [${joinedValues}]`)
      } else {
        callbackedChange(`${nameOpFragment} "${conditionState.value}"`)
      }
    }
  }, [valueIsValid, conditionState, callbackedChange])
  return (
    <Box display="flex" width="100%">
      <div style={{ flexGrow: 2, marginRight: 8 }}>
        <ValueAutocompleteField label="Field" name="name" options={nameDropdownOptions} />
      </div>
      {availableOps.length > 1 && (
        <div style={{ flexBasis: 150, flexGrow: 1, marginRight: 8 }}>
          <ValueAutocompleteField label="Operation" name="operation" options={availableOps} />
        </div>
      )}
      <Box flexGrow={3} display="flex" alignItems="center">
        <Box width="100%">
          <ValueField multiple={isMultiValue} options={valueOptions} fld={currentField} />
        </Box>
      </Box>
    </Box>
  )
}
